import React, { useEffect, useState } from "react";
import { useRfcLibrary } from "./RfcLibraryContext";
import { useParams } from "react-router-dom";
import { oktaAuth, oktaLoginAuth } from "../../api/oidcApi";
import { GetLocalStore } from "../../utils/storage";
import { ServiceApi } from "../../services/index.service";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FileRedirect = () => {
    const serviceapi = new ServiceApi();
    const params = useParams();
    const filePath = params["filePath"]
        ? params["filePath"] + (params["*"] ? "/" + params["*"] : "")
        : params["*"];

    // const { fileList } = useRfcLibrary();
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [fileUrl, setFileUrl] = useState("");
    const [numPages, setNumPages] = useState(null);
    const [pageWidth, setPageWidth] = useState(Math.min(window.innerWidth, 900));

    const isPdf = filePath?.split(".").pop()?.toLowerCase() === "pdf";

    useEffect(() => {
        const handleResize = () => setPageWidth(Math.min(window.innerWidth, 900));
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        async function handleRedirect() {
            setLoading(true);

            console.log("FileRedirect: filePath from params:", filePath);

            if (!filePath) {
                setError("Error: filePath is undefined.");
                setLoading(false);
                return;
            }

            // Check if authenticated
            let idToken = null;
            try {

                const tokenObj = await oktaAuth.tokenManager.get("idToken");
                idToken = tokenObj?.idToken || tokenObj?.value || tokenObj;
                console.log("FileRedirect: Retrieved id token from OktaAuth:", idToken);
            } catch (e) {
                idToken = GetLocalStore("token");
                console.log("FileRedirect: Error retrieving id token, falling back to localStorage:", idToken);
            }

            if (!idToken) {
                // RequireAuth already saved the path before redirecting here
                // Just trigger login - no need to save anything
                await oktaLoginAuth();

                setLoading(false);
                return;
            }
            // Authenticated — fetch from API
            try {
                const fileName = filePath.split('/').pop();
                console.log("FileRedirect: Querying fileName:", fileName);

                const data = await serviceapi.getRfcLibraryDataFromS3({
                    prefix: "", // Empty prefix to search all files
                    query: fileName
                }, {
                    headers: {
                        Authorization: idToken
                    }
                });
                let apiFiles = Array.isArray(data) ? data : data?.files || [];

                if (apiFiles.length > 0 && !apiFiles[0].path && apiFiles[0].file_name) {
                    apiFiles = apiFiles.map(f => ({ ...f, path: f.path || f.file_name }));
                }

                const file = apiFiles.find(f => f.path === filePath);
                const resolvedUrl = file && (file.open_url || file.presigned_url || file.download_url);
                if (resolvedUrl) {
                    setFileUrl(resolvedUrl);
                } else {
                    setError("File not found or cannot be opened. Check the file path and API response.");
                }
            } catch (e) {
                console.error("FileRedirect: API error:", e);
                setError("Error fetching file: " + (e?.message || e));
            }

            setLoading(false);
        }

        handleRedirect();
    }, [filePath]);

    if (fileUrl && isPdf) {
        // Rendered client-side via pdf.js instead of the browser's native PDF
        // plugin: Safari's iframe-embedded PDF viewer gets stuck on page 1 with
        // no scrolling for cross-origin PDFs, while a canvas-based renderer
        // behaves the same across every browser.
        return (
            <div style={{ width: "100%", minHeight: "100vh", background: "#525659", overflow: "auto", padding: "16px 0" }}>
                <Document
                    file={fileUrl}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    onLoadError={(err) => setError("Error loading PDF: " + (err?.message || err))}
                    loading={<div style={{ textAlign: "center", marginTop: "100px", color: "#fff" }}>Loading document...</div>}
                >
                    {Array.from(new Array(numPages || 0), (_, index) => (
                        <div key={`page_${index + 1}`} style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
                            <Page pageNumber={index + 1} width={pageWidth} />
                        </div>
                    ))}
                </Document>
                {error && <div style={{ color: "#ff8080", textAlign: "center", marginTop: "24px" }}>{error}</div>}
            </div>
        );
    }

    if (fileUrl) {
        return (
            <iframe
                src={fileUrl}
                title="File viewer"
                style={{ width: "100%", height: "100vh", border: "none" }}
            />
        );
    }

    return (
        <div style={{ textAlign: "center", marginTop: "100px" }}>
            <h2>Redirecting to file...</h2>
            {loading && !error && <div>Loading...</div>}
            {error && <div style={{ color: "red", marginTop: "24px" }}>{error}</div>}
        </div>
    );
};

export default FileRedirect;
